// Shared formatting for race placings — used by the bird profile's Race
// Results card and the Season Performance chart so the two never disagree.

// Default tier labels. Editable per result because the terminology differs
// between organisations (Club/Fed/Combine here; Section/Union/Federation
// elsewhere) — the label the fancier types is stored with the row.
export const TIER_DEFAULTS = ['Club', 'Fed', 'Combine'];

export const ordinal = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  const s = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;
  return num + (s[(v - 20) % 10] || s[v] || s[0]);
};

// "1st of 250" / "1st" / "of 250" for one tier, or null when empty.
export const tierText = (pos, birds) => {
  if ((pos === null || pos === undefined || pos === '') && (birds === null || birds === undefined || birds === '')) {
    return null;
  }
  const parts = [];
  if (pos !== null && pos !== undefined && pos !== '') parts.push(ordinal(pos));
  if (birds !== null && birds !== undefined && birds !== '') parts.push(`of ${birds}`);
  return parts.join(' ');
};

// The (label, pos, birds) triple for each of a result's three levels, in order.
const tierRows = (r) => [
  [r.t1_label, r.t1_pos, r.t1_birds],
  [r.t2_label, r.t2_pos, r.t2_birds],
  [r.t3_label, r.t3_pos, r.t3_birds]
];

// The non-empty tiers of one result as {label, text} entries. The formatter
// defaults to the profile card's wording ("1st of 250"); the season chart
// passes a compact one ("1/250") — the tier-extraction logic stays single.
export const resultTiers = (r, format = tierText) => {
  if (!r) return [];
  return tierRows(r)
    .map(([label, pos, birds]) => {
      const text = format(pos, birds);
      return text ? { label: label || '', text } : null;
    })
    .filter(Boolean);
};

// --- FCI World Best Pigeon coefficient -------------------------------------
// A pigeon's coefficient for a race weights its finishing position by the size
// of the field it beat — the bigger the field, the better a given position:
//
//   coefficient = position × 1000 ÷ min(birds, 5000)
//
// FCI caps the field at 5000 birds and quotes the result to three decimals;
// the LOWEST coefficient wins. A coefficient can only be worked out when the
// field size — the "out of (birds)" birdage — is known, so a placing entered
// without a bird count has no coefficient.
export const FCI_BIRD_CAP = 5000;

export const coefficient = (pos, birds) => {
  const p = Number(pos);
  const n = Number(birds);
  if (!Number.isFinite(p) || !Number.isFinite(n) || p <= 0 || n <= 0) return null;
  return (p * 1000) / Math.min(n, FCI_BIRD_CAP);
};

// Three-decimal string per FCI ("8.667"), or '' when not computable.
export const formatCoefficient = (value) =>
  value === null || value === undefined || !Number.isFinite(value) ? '' : value.toFixed(3);

export const coefficientText = (pos, birds) => formatCoefficient(coefficient(pos, birds));

// The non-empty levels of a result with their placing text AND coefficient:
// [{ level, label, text, coeffValue, coeff }]. `coeffValue` is the raw number
// (null when the field size is missing — no birdage, no coefficient) and
// `coeff` its three-decimal string. Shares the tier extraction with the
// formatters above so the two never drift apart.
export const resultTierCells = (r, format = tierText) => {
  if (!r) return [];
  return tierRows(r)
    .map(([label, pos, birds], level) => {
      const text = format(pos, birds);
      if (!text) return null;
      const coeffValue = coefficient(pos, birds);
      return { level, label: label || '', text, coeffValue, coeff: formatCoefficient(coeffValue) };
    })
    .filter(Boolean);
};
